/**
 * RequirementsDLCPanel.jsx
 *
 * Main panel rendered in the Canvas "Requirements" tab.
 *
 * - Reads requirements.md from the current project's IndexedDB
 * - Fetches DLC state (PR links, agent config, run history) from the BFF
 * - Merges and displays a grouped requirements table
 * - Hosts Configure / Run modals inline
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { parseRequirementsMarkdown, mergeWithDlcState } from '../../utils/requirementsParser';
import { getDlcRequirementsState, patchDlcRequirement, createImplementationRun } from '../../services/dlcService';
import ConfigureImplementationModal from './ConfigureImplementationModal';
import RunImplementationModal from './RunImplementationModal';
import PRCard from './PRCard';
import indexedDbFileStorage from '../../utils/indexedDbFileStorage';
import './RequirementsDLCPanel.css';

// ─── Status badge ────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  draft:      { label: 'Draft',      cls: 'status-draft' },
  'in review':{ label: 'In Review',  cls: 'status-review' },
  approved:   { label: 'Approved',   cls: 'status-approved' },
  closed:     { label: 'Closed',     cls: 'status-closed' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[(status || '').toLowerCase()] || { label: status || '—', cls: 'status-draft' };
  return <span className={`dlc-status-badge ${s.cls}`}>{s.label}</span>;
}

// ─── Priority badge ──────────────────────────────────────────────────────────

function PriorityBadge({ priority }) {
  const p = (priority || '').toLowerCase();
  const cls = p === 'very high' ? 'prio-very-high' : p === 'high' ? 'prio-high' : p === 'medium' ? 'prio-medium' : 'prio-low';
  return priority ? <span className={`dlc-prio-badge ${cls}`}>⊙ {priority}</span> : null;
}

// ─── Type badge ──────────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  if (!type) return null;
  return <span className="dlc-type-badge">{type}</span>;
}

// ─── Category tag ────────────────────────────────────────────────────────────

function CategoryTag({ category }) {
  if (!category) return null;
  const colors = {
    functional: '#60a5fa', security: '#f87171', performance: '#a78bfa',
    interface: '#34d399', data: '#fbbf24', accessibility: '#818cf8',
  };
  const color = colors[(category || '').toLowerCase()] || '#8888aa';
  return <span className="dlc-category-tag" style={{ color }}>{category}</span>;
}

// ─── Main panel ──────────────────────────────────────────────────────────────

export default function RequirementsDLCPanel({ projectContext }) {
  const [sections, setSections] = useState([]);
  const [dlcState, setDlcState] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configureReq, setConfigureReq] = useState(null);
  const [runReq, setRunReq] = useState(null);

  const projectId = projectContext?.projectId;
  const serviceName = projectContext?.selectedMicroservice?.name;
  const branch = projectContext?.revision?.name || 'main';

  // ─── Load requirements.md from IndexedDB ─────────────────────────────────

  /**
   * Fallback: scan all IndexedDB keys for this projectId to find requirements.md
   * without needing a service name upfront.
   */
  const findRequirementsInIndexedDB = useCallback(async () => {
    const searchNames = ['requirements.md', 'Requirements.md', 'REQUIREMENTS.MD'];
    await indexedDbFileStorage.init();
    const db = indexedDbFileStorage.db;
    if (!db) return null;

    return new Promise((resolve) => {
      const transaction = db.transaction(['files'], 'readonly');
      const store = transaction.objectStore('files');
      // Keys: `${projectId}::${serviceName}::${filePath}` — scan the prefix range.
      // Tilde ('~') sorts after all printable ASCII, bounding the prefix scan.
      const range = IDBKeyRange.bound(`${projectId}::`, `${projectId}::~`, false, true);
      const request = store.openCursor(range);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (!cursor) { resolve(null); return; }
        const parts = cursor.key.split('::');
        const filePath = parts.slice(2).join('::');
        const fileName = filePath.split('/').pop();
        if (searchNames.some(n => n.toLowerCase() === fileName.toLowerCase())) {
          resolve(cursor.value);
          return;
        }
        cursor.continue();
      };
      request.onerror = () => resolve(null);
    });
  }, [projectId]);

  const loadRequirements = useCallback(async () => {
    if (!projectId) {
      setError('No project loaded. Open a project to view requirements.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const searchPaths = ['requirements.md', 'Requirements.md', 'REQUIREMENTS.md', 'docs/requirements.md'];
      let markdownContent = null;

      // 1. Try known service names (selected microservice + configuration services)
      const knownServices = [
        ...(serviceName ? [serviceName] : []),
        ...Object.keys(projectContext?.configuration?.services || {}),
      ];

      for (const svc of knownServices) {
        for (const filePath of searchPaths) {
          const file = await indexedDbFileStorage.getFile(projectId, svc, filePath);
          if (file?.content) { markdownContent = file.content; break; }
        }
        if (markdownContent) break;
      }

      // 2. Fallback: scan all IndexedDB keys for this project
      if (!markdownContent) {
        const file = await findRequirementsInIndexedDB();
        if (file?.content) markdownContent = file.content;
      }

      if (!markdownContent) {
        setError('requirements.md not found in this project.\n\nPull the repository files first, or create a requirements.md file.');
        setSections([]);
        setLoading(false);
        return;
      }

      const parsed = parseRequirementsMarkdown(markdownContent);
      setSections(parsed.sections);

      // Fetch BFF state
      const state = await getDlcRequirementsState(projectId, branch);
      setDlcState(state);
    } catch (e) {
      setError(`Failed to load requirements: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [projectId, serviceName, branch, projectContext, findRequirementsInIndexedDB]);

  useEffect(() => { loadRequirements(); }, [loadRequirements]);

  // ─── Merge parsed requirements with BFF state ─────────────────────────────

  const getMergedSections = () =>
    sections.map(section => ({
      ...section,
      requirements: section.requirements.map(req =>
        mergeWithDlcState(req, dlcState[req.id])
      ),
    }));

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleSaveConfig = async (patch) => {
    await patchDlcRequirement(projectId, configureReq.id, patch);
    setDlcState(prev => ({
      ...prev,
      [configureReq.id]: { ...prev[configureReq.id], ...patch },
    }));
  };

  const handleRunStarted = async () => {
    if (!runReq) return;
    await createImplementationRun(projectId, runReq.id, runReq.agentConfig);
    await patchDlcRequirement(projectId, runReq.id, { status: 'In Review' });
    setDlcState(prev => ({
      ...prev,
      [runReq.id]: { ...prev[runReq.id], status: 'In Review', lastRunAt: new Date().toISOString() },
    }));
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="dlc-panel dlc-panel-loading">
        <div className="dlc-spinner" />
        <span>Loading requirements…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dlc-panel dlc-panel-error">
        <div className="dlc-error-icon">📋</div>
        <div className="dlc-error-text">{error}</div>
        <button className="dlc-retry-btn" onClick={loadRequirements} type="button">Retry</button>
      </div>
    );
  }

  const mergedSections = getMergedSections();
  const totalCount = mergedSections.reduce((n, s) => n + s.requirements.length, 0);

  return (
    <div className="dlc-panel">
      {/* Toolbar */}
      <div className="dlc-toolbar">
        <div className="dlc-toolbar-title">
          <span className="dlc-toolbar-icon">📋</span>
          Requirements
          <span className="dlc-total-badge">{totalCount}</span>
        </div>
        <button className="dlc-refresh-btn" onClick={loadRequirements} title="Refresh" type="button">↻</button>
      </div>

      {/* Table per section */}
      <div className="dlc-content">
        {mergedSections.map((section) => (
          <div key={section.id || section.title} className="dlc-section">
            <div className="dlc-section-header">
              <span className="dlc-section-title">{section.title}</span>
              {section.id && <span className="dlc-section-id">{section.id}</span>}
              <span className="dlc-section-count">{section.requirements.length} items</span>
            </div>

            <table className="dlc-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Priority</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>PR</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {section.requirements.map((req) => (
                  <tr key={req.id || req.title}>
                    <td className="dlc-td-title">
                      <div className="dlc-req-title">{req.title}</div>
                      {req.id && <div className="dlc-req-id">{req.id}</div>}
                    </td>
                    <td><PriorityBadge priority={req.priority} /></td>
                    <td><TypeBadge type={req.type} /></td>
                    <td><CategoryTag category={req.category} /></td>
                    <td><StatusBadge status={req.status} /></td>
                    <td>
                      {req.linkedPR
                        ? <PRCard linkedPR={req.linkedPR} />
                        : req.pr
                          ? <span className="dlc-pr-raw">{req.pr}</span>
                          : <span className="dlc-no-pr">—</span>
                      }
                    </td>
                    <td className="dlc-td-actions">
                      <button
                        className="dlc-action-btn"
                        title="Configure Implementation"
                        type="button"
                        onClick={() => setConfigureReq(mergeWithDlcState(req, dlcState[req.id]))}
                      >⚙</button>
                      <button
                        className={`dlc-action-btn dlc-run-btn ${req.agentConfig ? 'ready' : ''}`}
                        title="Run Implementation"
                        type="button"
                        onClick={() => setRunReq(mergeWithDlcState(req, dlcState[req.id]))}
                      >▶</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Modals */}
      {configureReq && (
        <ConfigureImplementationModal
          projectContext={projectContext}
          requirement={configureReq}
          onClose={() => setConfigureReq(null)}
          onSave={handleSaveConfig}
        />
      )}
      {runReq && (
        <RunImplementationModal
          requirement={runReq}
          onClose={() => setRunReq(null)}
          onRunStarted={handleRunStarted}
        />
      )}
    </div>
  );
}

RequirementsDLCPanel.propTypes = {
  projectContext: PropTypes.object,
};
